import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidsQuery,
  CreateCustomBidOrganisationMutation,
  CreateCustomBidOrganisationMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidQuery, updateSearchBidsQuery } from "./update_cache/updateBidEntityCache";

const CREATE_CUSTOM_BID_ORGANISATION_MUTATION = graphql(`
  mutation createCustomBidOrganisation($bidId: String!, $input: CreateCustomOrganisationInput!) {
    createCustomBidOrganisation(bidId: $bidId, input: $input) {
      id
      name
    }
  }
`);

export function useCreateCustomBidOrganisation(
  options?: UseMutationOptions<
    CreateCustomBidOrganisationMutation,
    unknown,
    CreateCustomBidOrganisationMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  return useGraphQlMutation(CREATE_CUSTOM_BID_ORGANISATION_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const newCustomOrg = data.createCustomBidOrganisation;
      const { bidId } = variables;

      // In the future, the cache logic will need to be updated if we add custom suppliers

      updateBidQuery(queryClient, bidId, (bid) => ({
        ...bid,
        customBuyers: [...(bid?.customBuyers ?? []), newCustomOrg],
      }));

      const allBidsQueries = queryClient.getQueryCache().findAll({ queryKey: ["bids"] });
      for (const { queryKey } of allBidsQueries) {
        const bidsData = queryClient.getQueryData<BidsQuery>(queryKey);
        if (!bidsData?.bids) continue;
        if (!bidsData.bids.results.some((b) => b.id === bidId)) continue;
        updateSearchBidsQuery(queryClient, queryKey, [bidId], (bid) => ({
          ...bid,
          customBuyers: [...(bid.customBuyers ?? []), newCustomOrg],
        }));
      }

      await options?.onSuccess?.(data, variables, context);
    },
  });
}
